import React from 'react';
import { useSnackbar } from 'notistack';
import {
  Table as MuiTable,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TableContainer,
  Typography,
  Box,
  TablePagination,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import QRCode from 'qrcode';
import { DateTime } from 'luxon';
import { ITransaction, TransactionTypeTranslations, ETransactionType } from 'common/transactions.interface';
import { BankingService } from 'services';
import { useBankingVisibility } from '../../../contexts/BankingVisibilityContext';
import Chip from './Chip';

interface Props {
  transactions: ITransaction[];
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
}

const Table = ({ transactions, page, rowsPerPage, total, onPageChange, onRowsPerPageChange }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { showValues } = useBankingVisibility();
  const bankingService = new BankingService();

  const handleCopy = (message: string, code?: string) => {
    if (code) {
      navigator.clipboard.writeText(code);
      enqueueSnackbar(message, { variant: 'success' });
    }
  };

  const handleDownloadQRCode = async (emvqrcps: string) => {
    try {
      const dataUrl = await QRCode.toDataURL(emvqrcps, {
        errorCorrectionLevel: 'M',
        margin: 2,
        width: 300,
      });

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'pix-qrcode.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      enqueueSnackbar('Erro ao gerar QR Code', { variant: 'error' });
    }
  };

  const handleShareEmvqrcps = async (emvqrcps: string) => {
    if (navigator.share) {
      try {
        await navigator.share({ text: emvqrcps });
        enqueueSnackbar('Código compartilhado!', { variant: 'success' });
      } catch (e) {
        enqueueSnackbar('Erro ao compartilhar código', { variant: 'error' });
      }
    } else {
      handleCopy('Código copiado!', emvqrcps);
    }
  };

  const handleDownloadBankSlip = async (transactionId: number) => {
    try {
      const { url } = await bankingService.getBankSlipPdf(transactionId);
      window.open(url, '_blank');
    } catch (error: any) {
      // Check for specific 400 error about PDF not being associated
      if (error?.response?.status === 400 && error?.response?.data?.detail === 'Este boleto não possui um arquivo PDF associado') {
        enqueueSnackbar('Boleto ainda em geração, tente novamente mais tarde', { variant: 'warning' });
      } else {
        enqueueSnackbar('Erro ao baixar boleto', { variant: 'error' });
      }
    }
  };

  const handleShareBankSlip = async (transactionId: number) => {
    try {
      const { url } = await bankingService.getBankSlipPdf(transactionId);
      handleCopy('Link de download copiado!', url);
    } catch (error: any) {
      // Check for specific 400 error about PDF not being associated
      if (error?.response?.status === 400 && error?.response?.data?.detail === 'Este boleto não possui um arquivo PDF associado') {
        enqueueSnackbar('Boleto ainda em geração, tente novamente mais tarde', { variant: 'warning' });
      } else {
        enqueueSnackbar('Erro ao obter link do boleto', { variant: 'error' });
      }
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <TableContainer>
        <MuiTable>
          <TableHead>
            <TableRow sx={{ bgcolor: '#F7F6F6' }}>
              {['Vencimento', 'Tipo', 'Valor', 'Pagador', 'Status', 'Ações'].map((h) => (
                <TableCell key={h} align="center">
                  <Typography variant="body1" fontWeight={500} color="#434241">
                    {h}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions?.map((t) => (
              <TableRow key={t.id} hover sx={{ borderBottom: '1px solid #E9E8E7' }}>
                <TableCell align="center">
                  <Typography variant="body2" align="center">
                    {t.bankSlipDetail?.payDueDate
                      ? DateTime.fromISO(t.bankSlipDetail.payDueDate).toFormat('dd/MM/yyyy')
                      : DateTime.fromISO(t.createdAt).toFormat('dd/MM/yyyy') || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" align="center">
                    {TransactionTypeTranslations[t.type] || t.type}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" align="right">
                    {showValues ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(t.value) : '••••••'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{t.bankSlipDetail?.payerName || t.counterpartyName || '-'}</Typography>
                </TableCell>
                <TableCell sx={{ display: 'flex', justifyContent: 'center' }} align="center">
                  <Chip transaction={t} />
                </TableCell>
                <TableCell align="center">
                  {t.type === ETransactionType.PIX_PAYMENT_IN && t.qrcode?.emvqrcps ? (
                    <>
                      <IconButton size="small" onClick={() => handleDownloadQRCode(t.qrcode.emvqrcps)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleCopy('Código PIX copiado!', t.qrcode.emvqrcps)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleShareEmvqrcps(t.qrcode.emvqrcps)}>
                        <ShareIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      {t.bankSlipDetail?.barcode ? (
                        <IconButton
                          size="small"
                          disabled={t.type !== ETransactionType.BANK_SLIP_IN || !t.bankSlipDetail?.barcode}
                          onClick={() => handleDownloadBankSlip(t.id)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <Tooltip title="O PDF do boleto está sendo processado, aguarde alguns instantes para que ele esteja disponível para download.">
                          <span>
                            <IconButton size="small" disabled onClick={() => handleDownloadBankSlip(t.id)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      <IconButton
                        size="small"
                        disabled={t.type !== ETransactionType.BANK_SLIP_IN || !t.bankSlipDetail?.barcode}
                        onClick={() => handleCopy('Código de barras copiado!', t.bankSlipDetail?.barcode || t.bankSlipDetail?.digitable)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={t.type !== ETransactionType.BANK_SLIP_IN || !t.bankSlipDetail?.barcode}
                        onClick={() => handleShareBankSlip(t.id)}
                      >
                        <ShareIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {(!transactions || transactions.length === 0) && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  Nenhum recebimento encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, p) => onPageChange(p)}
        onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
        labelRowsPerPage="Linhas por página:"
      />
    </Box>
  );
};

export default Table;
